import { useState, useEffect } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { 
  Shield, 
  Building2, 
  Users, 
  Settings, 
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Home
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthContext'
import { getFirebaseDb } from '@/lib/firebase'
import { doc, getDoc } from 'firebase/firestore'

const adminNavItems = [
  {
    title: 'Overview',
    href: '/admin',
    icon: BarChart3,
    description: 'Platform statistics'
  },
  {
    title: 'Organizations',
    href: '/admin/organizations',
    icon: Building2,
    description: 'Manage organizations'
  },
  {
    title: 'Users',
    href: '/admin/users',
    icon: Users,
    description: 'Manage users'
  },
  {
    title: 'Settings',
    href: '/admin/settings',
    icon: Settings,
    description: 'Platform settings'
  }
]

export function AdminPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [isAdmin, setIsAdmin] = useState(false)
  const [loading, setLoading] = useState(true)
  const [isCollapsed, setIsCollapsed] = useState(false)

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        navigate('/login')
        return
      }
      
      try {
        // Check Firebase Auth custom claims for is_super_admin
        const tokenResult = await user.getIdTokenResult()
        const isSuperAdmin = tokenResult.claims.is_super_admin === true
        
        if (isSuperAdmin) {
          setIsAdmin(true)
        } else {
          // Fallback: check Firestore user document
          const db = getFirebaseDb()
          const userDoc = await getDoc(doc(db, 'users', user.uid))
          
          if (userDoc.exists() && userDoc.data().role === 'super-admin') {
            setIsAdmin(true)
          } else {
            navigate('/organizations')
          }
        }
      } catch (error) {
        console.error('Error checking admin status:', error)
        navigate('/organizations')
      } finally {
        setLoading(false)
      }
    }
    
    checkAdminStatus()
  }, [user, navigate])

  const isActiveRoute = (href: string) => {
    if (href === '/admin') {
      return location.pathname === '/admin'
    }
    return location.pathname.startsWith(href)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!isAdmin) {
    return null
  }

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar */}
      <div className={cn(
        "relative flex flex-col h-screen bg-card border-r transition-all duration-300",
        isCollapsed ? "w-16" : "w-64"
      )}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          {!isCollapsed && (
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-red-500 to-orange-500 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Admin</h2>
              </div>
            </div>
          )}
          {isCollapsed && (
            <div className="w-8 h-8 bg-gradient-to-br from-red-500 to-orange-500 rounded-lg flex items-center justify-center mx-auto">
              <Shield className="w-5 h-5 text-white" />
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className={cn("h-8 w-8", isCollapsed && "mx-auto mt-2")}
          >
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </Button>
        </div>

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto py-4">
          <nav className="space-y-2 px-3">
            {adminNavItems.map((item) => {
              const isActive = isActiveRoute(item.href)
              const Icon = item.icon
              
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    "flex items-center space-x-3 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-accent hover:text-accent-foreground",
                    isActive && "bg-accent text-accent-foreground font-medium",
                    isCollapsed && "justify-center"
                  )}
                >
                  <Icon className="h-5 w-5 shrink-0" />
                  {!isCollapsed && (
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{item.title}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {item.description}
                      </div>
                    </div>
                  )}
                </Link>
              )
            })}
          </nav>
          
          {/* Back to App */}
          <div className="mt-4 px-3">
            <div className="border-t pt-4">
              <Link
                to="/organizations"
                className={cn(
                  "flex items-center space-x-3 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-accent hover:text-accent-foreground",
                  isCollapsed && "justify-center"
                )}
              >
                <Home className="h-5 w-5 shrink-0" />
                {!isCollapsed && (
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">Back to App</div>
                  </div>
                )}
              </Link>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="flex h-16 items-center justify-between px-6">
            <div className="flex items-center space-x-2">
              <Shield className="h-5 w-5 text-red-500" />
              <span className="font-semibold">Super Admin Panel</span>
            </div>
          </div>
        </header>
        
        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
